import React, { useState } from "react";
import {
  TableRow,
  TableCell,
  IconButton,
  Box,
  Typography,
  useTheme,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Tooltip
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import {
  stringFormatter,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { Category, CategoryNode } from "./types/categoryTypes";
import { formatCategoryPath } from "./utils/categoryUtils";
import {
  categoryActionsButtonSx,
  categoryChildCountChipSx,
  categoryExpandButtonSx,
  categoryFolderIconSx,
  categoryLevelChipSx,
  categoryMobileMetaSx,
  categoryNameIndentSx,
  categoryPathSx,
  categoryProductCountSx,
  getCategoryRowSx
} from "./categoryListStyles";

interface CategoryRowProps {
  category: CategoryNode;
  level?: number;
  selectedIds?: string[];
  isMobile?: boolean;
  onSelect?: (id: string) => void;
  onEdit: (cat: Category) => void;
  onDelete: (cat: Category) => void;
  onView?: (cat: Category) => void;
}

const getLevelLabel = (
  level: number,
  t: (
    key: string,
    fallback: string,
    options?: Record<string, unknown>
  ) => string
): string | null => {
  if (level === 0) return null;
  if (level === 1) return t("subcategory", "Subcategory");
  return t("category-level-n", "Level {{n}}", { n: level });
};

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  level = 0,
  selectedIds = [],
  isMobile = false,
  onSelect,
  onEdit,
  onDelete,
  onView
}) => {
  const theme = useTheme();
  const { t } = useKivunovaTranslation();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const hasChildren = category.children && category.children.length > 0;
  const childCount = category.children?.length ?? 0;
  const levelLabel = getLevelLabel(level, t);
  const isSelected = selectedIds.includes(category.id);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(category);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(category);
    handleMenuClose();
  };

  const handleView = () => {
    onView?.(category);
    handleMenuClose();
  };

  const handleToggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleRowClick = () => {
    if (hasChildren) {
      setOpen((prev) => !prev);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onSelect?.(category.id);
  };

  const getStatusColor = (
    status: string
  ): "success" | "default" | "error" | "warning" => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "active") return "success";
    if (statusLower === "inactive") return "default";
    if (statusLower === "archived") return "warning";
    return "default";
  };

  const getTranslatedStatus = (status?: string): string => {
    const normalized = (status || "active").toLowerCase().trim();
    return t(`${normalized}-status`, stringFormatter(normalized));
  };

  return (
    <>
      <TableRow
        hover
        onClick={handleRowClick}
        sx={{
          ...getCategoryRowSx(level, open),
          cursor: hasChildren ? "pointer" : "default"
        }}
      >
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          {onSelect && (
            <Checkbox
              checked={isSelected}
              onChange={handleCheckboxChange}
              size="small"
              color="primary"
            />
          )}
        </TableCell>

        <TableCell sx={{ py: isMobile ? 1 : 1.5 }}>
          <Box sx={categoryNameIndentSx(level, isMobile)}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={handleToggleExpand}
                sx={categoryExpandButtonSx(isMobile)}
                aria-label={
                  open
                    ? t("collapse-category", "Collapse category")
                    : t("expand-category", "Expand category")
                }
              >
                {open ? (
                  <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            ) : (
              <Box
                sx={{
                  width: isMobile ? 24 : 28,
                  mr: 0.5,
                  flexShrink: 0
                }}
              />
            )}

            <Box sx={categoryFolderIconSx(level, isMobile)}>
              {hasChildren && open ? (
                <FolderOpenIcon sx={{ fontSize: 18 }} />
              ) : (
                <FolderIcon sx={{ fontSize: 18 }} />
              )}
            </Box>

            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography
                  fontWeight={level === 0 ? 700 : 600}
                  sx={{
                    fontSize: isMobile
                      ? level === 0
                        ? 14
                        : 13
                      : level === 0
                        ? 15
                        : 14,
                    color: "text.primary",
                    lineHeight: 1.35,
                    wordBreak: "break-word"
                  }}
                >
                  {category.name}
                </Typography>

                {levelLabel && (
                  <Chip
                    label={levelLabel}
                    size="small"
                    sx={{
                      ...categoryLevelChipSx,
                      ...(isMobile && { height: 20, fontSize: 10 })
                    }}
                  />
                )}

                {hasChildren && !isMobile && (
                  <Chip
                    label={t(
                      "category-subcategory-count",
                      "{{count}} subcategories",
                      { count: childCount }
                    )}
                    size="small"
                    sx={categoryChildCountChipSx}
                  />
                )}
              </Box>

              {!isMobile && (
                <Typography variant="body2" sx={categoryPathSx}>
                  {formatCategoryPath(category.path)}
                </Typography>
              )}

              {isMobile && (
                <Box sx={categoryMobileMetaSx}>
                  <Box component="span" sx={categoryProductCountSx}>
                    {category.productCount || 0}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {(category.productCount || 0) === 1
                      ? t("item", "item")
                      : t("items", "items")}
                  </Typography>
                  <Chip
                    label={getTranslatedStatus(category.status)}
                    color={getStatusColor(category.status || "active")}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: 10, height: 20 }}
                  />
                  {hasChildren && (
                    <Typography variant="caption" color="text.secondary">
                      {t(
                        "category-subcategory-count",
                        "{{count}} subcategories",
                        { count: childCount }
                      )}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </TableCell>

        {!isMobile && (
          <TableCell>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Box component="span" sx={categoryProductCountSx}>
                {category.productCount || 0}
              </Box>
              {(category.productCount || 0) !== 1 ? (
                <Typography variant="caption" color="text.secondary">
                  {t("items", "items")}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("item", "item")}
                </Typography>
              )}
            </Box>
          </TableCell>
        )}

        {!isMobile && (
          <TableCell>
            <Chip
              label={getTranslatedStatus(category.status)}
              color={getStatusColor(category.status || "active")}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 12 }}
            />
          </TableCell>
        )}

        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title={t("more-actions", "More actions")}>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-label={t("category-actions", "Category actions")}
              aria-controls={anchorEl ? "category-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? "true" : undefined}
              sx={categoryActionsButtonSx(Boolean(anchorEl))}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            id="category-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 180,
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: theme.shadows[2]
                }
              }
            }}
          >
            <MenuItem onClick={handleView}>
              <ListItemIcon>
                <VisibilityOutlinedIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText>{t("view-details", "View Details")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>{t("edit-category", "Edit Category")}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete}>
              <ListItemIcon>
                <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>
                {t("delete-category", "Delete Category")}
              </ListItemText>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      {hasChildren &&
        open &&
        category.children!.map((child) => (
          <CategoryRow
            key={child.id}
            category={child}
            level={level + 1}
            selectedIds={selectedIds}
            isMobile={isMobile}
            onSelect={onSelect}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
          />
        ))}
    </>
  );
};

export default CategoryRow;
